import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { API_URL } from "./utils/utils";
import Swal from "sweetalert2";
import Footer from "../components/footer";
import Header from "../components/header";
import SeoLinksSection from "../components/SeoLinksSection";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [passwordData, setPasswordData] = useState({
    password: "",
    password_confirmation: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if token and email are present
    if (!token || !email) {
      Swal.fire({
        icon: "error",
        title: "Invalid Link",
        text: "The password reset link is invalid or has expired.",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/forgot-password");
      });
    }
  }, [token, email, navigate]);

  const handleChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log("Reset password form submitted", { token, email, passwordData });

    // Validate passwords match
    if (passwordData.password !== passwordData.password_confirmation) {
      Swal.fire({
        icon: "warning",
        title: "Password Mismatch",
        text: "Passwords do not match. Please try again.",
      });
      return;
    }

    // Validate password length
    if (passwordData.password.length < 8) {
      Swal.fire({
        icon: "warning",
        title: "Password Too Short",
        text: "Password must be at least 8 characters long.",
      });
      return;
    }

    if (!token || !email) {
      Swal.fire({
        icon: "error",
        title: "Invalid Link",
        text: "The password reset link is invalid or has expired.",
      });
      return;
    }

    setLoading(true);

    try {
      console.log("Resetting password with token:", token);

      const response = await fetch(`${API_URL}passenger/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: email,
          token: token,
          password: passwordData.password,
          password_confirmation: passwordData.password_confirmation,
        }),
      });

      console.log("Response status:", response.status);
      const data = await response.json();
      console.log("Response data:", data);

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Password Reset Successful",
          text:
            data.message ||
            "Your password has been reset successfully. You can now login with your new password.",
          confirmButtonText: "Go to Login",
        }).then(() => {
          navigate("/");
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Reset Failed",
          text:
            data.message ||
            data.error ||
            "Failed to reset password. The link may have expired. Please request a new one.",
        });
      }
    } catch (error) {
      console.error("Reset password error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <>
        <Header />
        <section
          className="banner pt-10 pb-10"
          style={{ backgroundImage: "url(images/testimonial.png)" }}
        >
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6 col-md-8">
                <div className="card shadow-lg border-0">
                  <div className="card-body p-5 text-center">
                    <h3 className="mb-4">Invalid Reset Link</h3>
                    <p className="text-muted mb-4">
                      The password reset link is invalid or has expired.
                    </p>
                    <Link to="/forgot-password" className="nir-btn">
                      Request New Reset Link
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <SeoLinksSection />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <section
        className="banner pt-10 pb-10"
        style={{ backgroundImage: "url(images/testimonial.png)" }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              <div className="card shadow-lg border-0">
                <div className="card-body p-5">
                  <h3 className="text-center mb-4">Reset Password</h3>
                  <p className="text-center text-muted mb-4">
                    Please enter your new password below.
                  </p>
                  <form
                    onSubmit={(e) => {
                      console.log("Form onSubmit event fired");
                      handleSubmit(e);
                    }}
                    noValidate
                  >
                    <div className="form-group mb-3">
                      <label htmlFor="password" className="form-label">
                        New Password
                      </label>
                      <input
                        type="password"
                        className="form-control p-3"
                        id="password"
                        name="password"
                        value={passwordData.password}
                        onChange={handleChange}
                        placeholder="Enter new password (min. 8 characters)"
                        minLength="8"
                        required
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label
                        htmlFor="password_confirmation"
                        className="form-label"
                      >
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        className="form-control p-3"
                        id="password_confirmation"
                        name="password_confirmation"
                        value={passwordData.password_confirmation}
                        onChange={handleChange}
                        placeholder="Confirm new password"
                        minLength="8"
                        required
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="form-group mb-3">
                      <button
                        type="button"
                        className="nir-btn w-100"
                        disabled={loading}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log("Reset password button clicked");
                          
                          // Get the form element
                          const form = e.target.closest('form');
                          if (form) {
                            // Check form validity
                            if (form.checkValidity()) {
                              handleSubmit(e);
                            } else {
                              // Trigger browser validation
                              form.reportValidity();
                            }
                          } else {
                            // Fallback: call handler directly
                            handleSubmit(e);
                          }
                        }}
                      >
                        {loading ? "Resetting..." : "Reset Password"}
                      </button>
                    </div>
                    <div className="text-center">
                      <Link to="/" className="text-decoration-none">
                        <i className="fa fa-arrow-left me-2" /> Back to Home
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <SeoLinksSection />
      <Footer />
    </>
  );
}
